import { publicClient, privateClient } from '../apiClient';
import { DEMO_COURSES, DEMO_COURSE_OUTLINE, DEMO_COURSE_OUTLINE_FLAT } from '../../mocks/course/courseMocks';
import type { Course, CourseChapter } from "../../types/course/Course";
import type { Neighbours } from "../../types/course/Neighbours";

class CourseService {
    private useMock = import.meta.env.VITE_USE_MOCK === 'true';

    async fetchAll(): Promise<Course[]> {
        if (this.useMock) return DEMO_COURSES;
        const response = await publicClient.get('/courses');
        return response.data;
    }

    async fetchById(courseId: string): Promise<Course> {
        if (this.useMock) return DEMO_COURSES.find((c) => c.id === courseId) ?? null;
        const response = await publicClient.get(`/courses/${courseId}`);
        return response.data;
    }

    async fetchEnrolled(): Promise<Course[]> {
        if (this.useMock)
            return DEMO_COURSES;
        const response = await privateClient.get('/courses/enrolled');
        return response.data;
    }

    async fetchOutline(courseId: string): Promise<CourseChapter[]> {
        if (this.useMock)
            return DEMO_COURSE_OUTLINE;
        const response = await publicClient.get(`/courses/${courseId}/outline`);
        return response.data;
    }

    async fetchNextAndPrevLesson(courseId: string, lessonId: string): Promise<Neighbours> {
        if (this.useMock) {
            const flat = DEMO_COURSE_OUTLINE_FLAT;
            const currentIndex = flat.findIndex(item => item.id === lessonId);
            const outline = DEMO_COURSE_OUTLINE;
            const chapterTitle = outline.find(ch =>
                ch.sections.some(s =>
                    s.subsections.some(ss => ss.id === lessonId)
                )
            )?.title ?? '';
            const completed = flat.filter(item => item.completed).length;

            return {
                prev: flat[currentIndex - 1] ?? null,
                next: flat[currentIndex + 1] ?? null,
                current: currentIndex + 1,
                total: flat.length,
                chapterTitle,
                lessonTitle: flat[currentIndex]?.title ?? '',
                percent: Math.round((completed / flat.length) * 100),
                completed: completed,
            };
        }
        const response = await publicClient.get(`/courses/${courseId}/lessons/${lessonId}/neighbours`);
        return response.data;
    }

    async fetchCompletedIds(courseId: string): Promise<string[]> {
        if (this.useMock)
            return DEMO_COURSE_OUTLINE_FLAT.filter(item => item.completed).map(item => item.id);
        const response = await privateClient.get(`/courses/${courseId}/completed`);
        return response.data;
    }

    async enrollCourse(courseId: string): Promise<{ success: boolean }> {
        const response = await privateClient.post(`/courses/${courseId}/enroll`);
        return response.data;
    }

    async isEnrolled(courseId: string): Promise<boolean> {
        const response = await privateClient.get(`/courses/${courseId}/enrolled/check`);
        return response.data.enrolled;
    }
}

export default new CourseService();